import { useEffect, useRef, useState } from "react";
import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FileText, LayoutDashboard, LogOut, Mail, Megaphone, MessageSquare, MessagesSquare, Plane, Settings, ShieldCheck, UserCog, Users, UsersRound, Video } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDemo } from "@/store/DemoStore";
import { api } from "@/lib/api";
import { Avatar, Logo, avatarUrl } from "@/components/common";
import FeedbackButton from "@/components/FeedbackButton";
import NotificationBell from "@/components/NotificationBell";
import InstallPrompt from "@/components/InstallPrompt";
import { ensurePushSubscribed } from "@/lib/push";

const NAV = [
  { to: "/agent", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/agent/clients", label: "Clients", icon: Users, id: "clients" },
  { to: "/agent/trips", label: "Trips", icon: Plane, id: "trips" },
  { to: "/agent/groups", label: "Groups", icon: UsersRound, id: "groups" },
  { to: "/agent/quotes", label: "Quotes", icon: FileText, id: "quotes" },
  { to: "/agent/settings", label: "Settings", icon: Settings, id: "settings" },
];

const ADMIN = [
  { to: "/agent/users", label: "Users", icon: UserCog, id: "users" },
  { to: "/agent/agents", label: "Agents", icon: ShieldCheck, id: "agents" },
  { to: "/agent/communication", label: "Communication", icon: MessagesSquare, id: "communication" },
  { to: "/agent/marketing", label: "Marketing", icon: Megaphone, id: "marketing" },
];

const Badge = ({ n, className }) => (n > 0 ? <span data-testid="unread-badge" className={cn("grid h-5 min-w-5 place-items-center rounded-full bg-sky-500 px-1.5 text-[10px] font-bold text-white", className)}>{n}</span> : null);

const JITSI_ROOM_URL = "https://meet.jit.si/MyCompassConferenceRoom";
const QUICK_LINKS = [
  { id: "gateway", label: "Gateway Travel", href: "https://portal.gatewaytravel.com/auth/login", glyph: "GT" },
  { id: "email", label: "Business Email", href: "https://id.ionos.com/identifier?client_app=IONOSMAIL", icon: Mail },
  { id: "conference", label: "Conference Room", icon: Video, popup: true },
];

function QuickLinks() {
  const [hovered, setHovered] = useState(null);
  const [active, setActive] = useState(false);
  const popupRef = useRef(null);
  const hbRef = useRef(null);
  const watchRef = useRef(null);

  const fetchPresence = () => api.get("/conference/presence").then(({ data }) => setActive(!!data.inRoom)).catch(() => {});
  useEffect(() => {
    fetchPresence();
    const t = setInterval(fetchPresence, 20000);
    return () => { clearInterval(t); clearInterval(hbRef.current); clearInterval(watchRef.current); };
  }, []);

  const stopHeartbeat = () => {
    clearInterval(hbRef.current); hbRef.current = null;
    clearInterval(watchRef.current); watchRef.current = null;
    popupRef.current = null;
    api.post("/conference/leave").then(({ data }) => setActive(!!data.inRoom)).catch(() => {});
  };
  const openConference = () => {
    popupRef.current = window.open(JITSI_ROOM_URL, "CompassConferenceRoom", "popup,width=1100,height=760");
    api.post("/conference/heartbeat").then(() => setActive(true)).catch(() => {});
    clearInterval(hbRef.current);
    hbRef.current = setInterval(() => api.post("/conference/heartbeat").catch(() => {}), 15000);
    clearInterval(watchRef.current);
    watchRef.current = setInterval(() => { if (!popupRef.current || popupRef.current.closed) stopHeartbeat(); }, 3000);
  };

  const cls = "relative grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white ring-1 ring-white/10 transition-colors hover:bg-white/20 hover:ring-white/25";
  return (
    <div className="space-y-2" data-testid="sidebar-quick-links">
      <div className={cn("text-center text-xs font-medium text-white/90 transition-opacity duration-150", hovered ? "opacity-100" : "opacity-0")} aria-hidden={!hovered} data-testid="quick-link-tooltip">{hovered || "\u00A0"}</div>
      <div className="flex items-center justify-center gap-3">
        {QUICK_LINKS.map((q) => {
          const inner = q.glyph ? <span className="font-display text-xs font-bold tracking-tight leading-none">{q.glyph}</span> : <q.icon className="h-4 w-4" strokeWidth={1.75} />;
          const label = q.id === "conference" && active ? "Conference Room · in session" : q.label;
          const handlers = { onMouseEnter: () => setHovered(label), onMouseLeave: () => setHovered(null), onFocus: () => setHovered(label), onBlur: () => setHovered(null) };
          const dot = q.id === "conference" && active ? <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-red-500 ring-2 ring-navy-900" data-testid="conference-active-dot" /> : null;
          return q.popup ? (
            <button key={q.id} type="button" onClick={openConference} aria-label={label} data-testid={`quick-link-${q.id}`} className={cls} {...handlers}>{inner}{dot}</button>
          ) : (
            <a key={q.id} href={q.href} target="_blank" rel="noreferrer" aria-label={label} data-testid={`quick-link-${q.id}`} className={cls} {...handlers}>{inner}</a>
          );
        })}
      </div>
    </div>
  );
}

function SideLink({ item, badge }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`agent-nav-${item.id}`}
      className={({ isActive }) => cn("flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors", isActive ? "bg-white text-navy-900" : "text-white/65 hover:bg-white/5 hover:text-white")}
    >
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
      <span className="flex-1">{item.label}</span>
      <Badge n={badge} />
    </NavLink>
  );
}

function Sidebar({ unread, settings, isAdmin, onLogout }) {
  return (
    <aside className="hidden w-64 shrink-0 flex-col bg-navy-900 px-4 py-6 lg:flex" data-testid="agent-sidebar">
      <div className="px-2"><Logo light sub="Powered by Topsail Travel" /></div>
      <nav className="mt-10 space-y-1">{NAV.map((n) => <SideLink key={n.id} item={n} />)}</nav>
      <p className="mb-2 mt-8 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35">Inbox</p>
      <SideLink item={{ to: "/agent/messages", label: "Messages", icon: MessageSquare, id: "messages" }} badge={unread} />
      {isAdmin && <><p className="mb-2 mt-8 px-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/35">Admin</p>{ADMIN.map((n) => <SideLink key={n.id} item={n} />)}</>}
      <div className="mt-auto space-y-3">
        <QuickLinks />
        <div className="flex items-center gap-3 rounded-xl bg-white/5 p-3">
          <Avatar name={settings.advisorName} src={avatarUrl(settings.advisorId, settings.advisorAvatar)} size="sm" className="bg-white/10 text-white ring-white/10" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{settings.advisorName}</p>
            <p className="truncate text-xs text-white/50">{settings.advisorTitle}</p>
          </div>
        </div>
        <button onClick={onLogout} data-testid="switch-portal-link" className="flex items-center gap-2 px-3 text-xs font-medium text-white/50 transition-colors hover:text-white">
          <LogOut className="h-3.5 w-3.5" /> Sign Out
        </button>
      </div>
    </aside>
  );
}

const TABS = [NAV[0], NAV[1], NAV[2], NAV[3], { to: "/agent/messages", label: "Messages", icon: MessageSquare, id: "messages" }];

function MobileTabs({ unread }) {
  return (
    <nav className="hide-on-kb pb-safe grid shrink-0 grid-cols-5 border-t border-slate-200 bg-white px-1 pt-1.5 lg:hidden" data-testid="agent-mobile-tabs">
      {TABS.map((t) => (
        <NavLink key={t.id} to={t.to} end={t.end} data-testid={`agent-tab-${t.id}`} className={({ isActive }) => cn("relative flex flex-col items-center gap-1 rounded-lg py-1.5 text-[11px] font-medium", isActive ? "text-navy-900" : "text-slate-400")}>
          <t.icon className="h-5 w-5" strokeWidth={1.75} />
          {t.label}
          {t.id === "messages" && <Badge n={unread} className="absolute right-[18%] top-0" />}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AgentLayout() {
  const { conversations, settings, isAdmin, logout } = useDemo();
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate("/portal"); };
  const unread = conversations.reduce((s, c) => s + c.unreadAgent, 0);
  const { pathname } = useLocation();
  const main = useRef(null);
  const isMessages = pathname.startsWith("/agent/messages");

  useEffect(() => {
    if (main.current) main.current.scrollTop = 0;
  }, [pathname]);
  useEffect(() => { if (settings.pushEnabled) ensurePushSubscribed(); }, [settings.pushEnabled]);

  return (
    <div className="app-shell flex bg-[#F5F7FA]" data-testid="agent-portal">
      <Sidebar unread={unread} settings={settings} isAdmin={isAdmin} onLogout={onLogout} />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 lg:hidden">
          <Logo size="h-5" />
          <div className="flex items-center gap-1">
            <FeedbackButton variant="icon" />
            <NotificationBell enabled={!!settings.pushEnabled} />
            <Link to="/agent/settings" data-testid="agent-mobile-settings" aria-label="Settings" className="grid h-10 w-10 place-items-center rounded-full text-navy-700 hover:bg-slate-100"><Settings className="h-5 w-5" /></Link>
            <button onClick={onLogout} data-testid="agent-mobile-exit" aria-label="Sign out" className="grid h-10 w-10 place-items-center rounded-full text-navy-700 hover:bg-slate-100"><LogOut className="h-5 w-5" /></button>
          </div>
        </header>
        <main ref={main} className={cn("min-h-0 flex-1", isMessages ? "overflow-hidden" : "scroll-area")}>
          {isMessages ? <Outlet /> : <div className="fade-in mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 lg:px-10 lg:py-10" key={pathname}><Outlet /></div>}
        </main>
        <MobileTabs unread={unread} />
      </div>
      <FeedbackButton className="hidden bottom-6 right-6 lg:grid" />
      <InstallPrompt brand="mycompass" />
    </div>
  );
}
